import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './styles/ActivitySummary.css';
import calculateSpeedAndPace from '../utils/calculateSpeedAndPace';
import GeoLocationComponent from './GeoLocationComponent';
import ElevationChart from './ElevationChart';
import StrengthHeartRateHistogram from './Strength/StrengthHeartRateHistogram';
import WellnessForm from './ActivityDetails/WellnessForm';
import ElevationProfile from './ElevationProfile';
import Graphics from './Graphics';
import ZoneDistributionChart from './AthleteDashboard/ZoneDistributionChart';
import ZoneDistributionStrava from './AthleteDashboard/ZoneDistributionStrava';
import Summary from './Summary';
import Dashboard from './Dashboard/Dashboard';
import CyclingMetricsTable from './Cycling/CyclingMetricsTable';
import LapsTableCycling from './Dashboard/LapsTableCycling';
import { calculateAverageGap } from '../utils/GapMedioCalculator';
import { calculateTSSWithTerrain } from '../utils/tssUtils';
import { interpretTTE, interpretTAE } from '../utils/interpretTraining';
import { calculateVO2Max } from '../utils/vo2Utils';
import { estimateFTPFrom20MinTest } from '../utils/FTP';
import { calculateNormalizedPower } from '../utils/normalizedPower';
import { calculateWKg } from '../utils/wkgCalculator';
import PowerChart from './Cycling/PowerChart';
import ZoneDistributionChartCycling from './AthleteDashboard/ZoneDistributionChartCycling';
import RunningPhysiologicalAnalysis from './Running/RunningPhysiologicalAnalysis';
import SessionAiSummaryPanel from './Insights/SessionAiSummaryPanel';
import { useTranslation } from '../i18n';
import API_BASE_URL from '../config/api';




const API_BASE = API_BASE_URL;

const ActivitySummary = ({ fileId, activityId, onSummaryData, onSave, athleteId: athleteIdProp, date, hasHeartrate }) => {
  const { t, language } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [totalElevationGain, setTotalElevationGain] = useState(0);
  const [userId, setUserId] = useState(null);
  const [banister, setBanister] = useState({});
  const [planned, setPlanned] = useState({
    duracion: '00:00:00',
    distancia: '0',
    ritmo_medio: '0',
    calorias: '0',
    desnivel_positivo: '0',
    tte: '0',
    tea: '0',
    tss: '0',
  });
  
   const lastSentRef = useRef(null);
  
   useEffect(() => {
          const storedUserId = localStorage.getItem("userId");
          const pmcId = athleteIdProp || storedUserId;
          if (storedUserId) {
                setUserId(pmcId);
                axios.get(`${API_BASE}/api/analytics/athlete/${pmcId}/pmc`)
                  .then(r => {
                    if (r.data && r.data.ctl !== undefined) {
                      setBanister({ ctl: r.data.ctl, atl: r.data.atl, tsb: r.data.tsb, ramp7d: r.data.ramp7d });
                    }
                  })
                  .catch(() => {});
          } else {
                console.warn("⚠️ No se encontró userId en localStorage.");
          }
	}, [athleteIdProp]);

 

  useEffect(() => {
    if (!activityId) return;
    const fetchActivity = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/activities/${activityId}`);
		if (response.data?.planned) {
          setPlanned(response.data.planned);
        }
      } catch (error) {
        console.error('❌ Error al cargar actividad para planned:', error);
      }
    };
    fetchActivity();
  }, [activityId]);

  const convertSecondsToTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.round(seconds % 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

useEffect(() => {
  if (!fileId || !activityId) return;

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE}/api/fit/parsed-data/${fileId}`);
      const records = response.data.records || [];

//      console.log("🧪 Total de records disponibles:", records.length);
//      console.log("🔍 Primeros 5 valores de potencia:", records.slice(0, 5).map(r => r.power));

      const sessionData = Array.isArray(response.data.sessions)
        ? response.data.sessions[0]
        : response.data.session;

      const { avgPace, maxPace, avgSpeed, maxSpeed } = calculateSpeedAndPace({
        ...response.data,
        sessions: sessionData,
      });

//      console.log("📌 Laps sin procesar:", response.data.laps?.slice(0, 3));

      const formattedLaps = (response.data.laps || []).map((lap) => ({
        total_elapsed_time: lap.total_elapsed_time || 0,
        distance: (lap.total_distance || 0) / 1000,
        pace: 60 / ((lap.enhanced_avg_speed || 1) * 3.6),
        elevation: lap.total_ascent || 0,
      }));

      setData(response.data);

      const gap = calculateAverageGap(formattedLaps);
      const ftpEstimated = estimateFTPFrom20MinTest(records);
      const normalizedPower = calculateNormalizedPower(records);
      const pesoKg = response.data?.profile?.weight ?? 70;
      const wKg = calculateWKg(ftpEstimated, pesoKg);

	const tssPayload = {
	  activity: {
		totalTimerTime: sessionData.total_timer_time,
		distance: (sessionData.total_distance || 0) / 1000,
	  },
	  
	sessions: {
	  sport: sessionData?.sport || response.data?.activity?.sport || "running",
	  avgHeartRate: sessionData.avg_heart_rate,
	  avgPace: avgPace,
	  gapPace: gap, // ✅ NUEVO
	},

	  zones_target: response.data?.zones_target,
	 user: {
	  hrMax: response.data?.profile?.hr_max ?? response.data?.profile?.hrMax ?? null,
	  runningThresholdPace: response.data?.profile?.runningThresholdPace ?? null,
	},
 };




      // Leer TSS desde backend (processeddatas.metrics.tss) - mas confiable que calcular en frontend
      const backendTss = response.data?.metrics?.tss;
      const { tss: calcTss, message } = calculateTSSWithTerrain(tssPayload, { language });
      const tss = (Number.isFinite(Number(backendTss)) && Number(backendTss) > 0 && Number(backendTss) < 1000)
        ? Number(backendTss)
        : (Number.isFinite(Number(calcTss)) && Number(calcTss) < 1000 ? Number(calcTss) : undefined);

      let totalGain = sessionData?.total_ascent || 0;
      if (!totalGain && response.data.laps?.length > 0) {
        totalGain = response.data.laps.reduce((sum, lap) => sum + (lap.total_ascent || 0), 0);
      }
      setTotalElevationGain(totalGain);

      const completedData = {
        duracion: convertSecondsToTime(response.data.activity?.total_timer_time || 0),
        distancia: ((sessionData.total_distance || 0) / 1000).toFixed(2),
        ritmo_medio: avgPace,
        calorias: sessionData.total_calories || 0,
        desnivel_positivo: totalGain,
        tte: sessionData.total_training_effect ?? 0,
        tea: sessionData.total_anaerobic_effect ?? 0,
        tss: (Number.isFinite(Number(tss)) && Number(tss) < 1000) ? Number(tss) : undefined,
        gap: gap || '-',
        vo2max: calculateVO2Max(((sessionData.total_distance || 0) / 1000), sessionData.total_timer_time),
        avgPower: response.data.completed?.avgPower ?? sessionData.avg_power ?? null,
        maxPower: response.data.completed?.maxPower ?? sessionData.max_power ?? null,
        totalWork: response.data.completed?.totalWork ?? sessionData.total_work ?? null,
        normalizedPower: response.data.completed?.normalizedPower ?? normalizedPower ?? null,
        ftp: response.data.completed?.ftp ?? ftpEstimated ?? response.data?.zones_target?.functional_threshold_power ?? null,
        intensityFactor: response.data.completed?.intensityFactor ??
          (normalizedPower && ftpEstimated ? (normalizedPower / ftpEstimated).toFixed(2) : null),
        wKg: response.data.completed?.wKg ?? wKg ?? null,
        avgHeartRate: sessionData.avg_heart_rate ?? null,
        maxHeartRate: sessionData.max_heart_rate ?? null,
        calories: sessionData.total_calories || 0,
      };

      const metrics = {
        ritmo_medio: { avg: avgPace, max: maxPace },
        ritmo_cardiaco: {
          avg: sessionData.avg_heart_rate || 'N/A',
          max: sessionData.max_heart_rate || 'N/A'
        },
        cadencia: {
          avg: sessionData.avg_cadence ?? 'N/A',
          max: sessionData.max_cadence ?? response.data.activity?.max_cadence ?? 'N/A'
        },
        velocidad: {
          avg: avgSpeed,
          max: maxSpeed
        }
      };

      const summaryPayload = {
        completed: completedData,
        metrics,
        tssMessage: message,
        teMessage: interpretTTE(completedData.tte, language),
        teaMessage: interpretTAE(completedData.tea, language),
      };

      setSummary(summaryPayload);

    } catch (err) {
      console.error('Error al cargar los datos:', err);
      setError(t('activitySummary.loadError'));
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [fileId, activityId, planned, t, language]);


useEffect(() => {
  if (!summary || typeof onSummaryData !== 'function') return;

  const summaryString = JSON.stringify(summary);

  // Verificar si hay datos válidos antes de enviar
  const hasCompletedData = summary.completed && Object.keys(summary.completed).length > 0;
  const hasMetricsData = summary.metrics && Object.keys(summary.metrics).length > 0;

  if (lastSentRef.current !== summaryString && (hasCompletedData || hasMetricsData)) {
    lastSentRef.current = summaryString;
    onSummaryData(summary);

    axios
      .put(`${API_BASE}/api/activities/update-completed/${activityId}`, {
        completed: hasCompletedData ? summary.completed : undefined,
        metrics: hasMetricsData ? summary.metrics : undefined,
      })
      .then(() => {
//        console.log('✅ Datos completados y métricas guardados correctamente.');
      })
      .catch((err) => {
        console.error('❌ Error al guardar los datos completados:', err);
      });

    if (typeof onSave === 'function') onSave();
  }
}, [summary, onSummaryData, onSave, activityId]);

 
  if (loading) return <div>{t('activitySummary.loading')}</div>;
  if (error) return <div>{error}</div>;
  if (!summary) return <div>{t('activitySummary.noData')}</div>;

  const completed = summary.completed;
  const sport = (
    data?.activity?.sport ||
    data?.session?.sport ||
    data?.sessions?.[0]?.sport ||
    ''
  ).toLowerCase();

  const isCycling = sport.toLowerCase() === 'cycling';
  const isRunning = sport.toLowerCase() === 'running';
  const isOtherSport = !isCycling && !isRunning;

//  console.log("🔍 Zone Chart Debug", { isRunning, isCycling, userId, fileId });

return (
  <div className="activity-summary-wrapper">
    <div className="summary-container">
      {isCycling && (
        <CyclingMetricsTable
          summary={summary}
          data={data}
          session={Array.isArray(data?.sessions) ? data.sessions[0] : data?.session}
        />
      )}

      <h2>{t('activitySummary.title')}</h2>
      {!isCycling && <Summary fileId={fileId} completed={completed} />}
      {isCycling && <PowerChart records={data?.records || []} laps={data?.laps || []} />}


      <h2>{t('activitySummary.routeMap')}</h2>
      <div className="map-container">
        <GeoLocationComponent fileId={fileId} />
      </div>

      {isRunning && (
        <>
          <div className="section-block">
            <ElevationProfile fileId={fileId} />
          </div>

          <div className="section-block">
            <div style={{ fontSize: 13, fontWeight: 700, color: '#193255', marginBottom: 6 }}>
              {t('elevationProfile.consolidatedChartTitle')}
            </div>
            <div className="chart-container">
              <ElevationChart fileId={fileId} />
            </div>
          </div>
    {/* 🔬 Análisis Fisiológico para Running */}
    <RunningPhysiologicalAnalysis completed={completed} metrics={summary} athleteId={userId} activityId={activityId} banister={banister} date={date} />
        </>
      )}

     {(isRunning || isCycling) && (
  <div className="section-block">
    <h2>{t('activitySummary.zones')}</h2>

    {/* Consola para depurar si el componente se intenta renderizar */}
    {console.log("🟩 Render Condición:", { isRunning, isCycling, userId, fileId })}

    {isRunning && (
      String(fileId || '').startsWith('strava-')
        ? <ZoneDistributionStrava userId={userId} fileId={fileId} sport="correr" />
        : <ZoneDistributionChart userId={userId} fileId={fileId} />
    )}
    
    {isCycling && (
      <>
        {console.log("🚴 Renderizando ZoneDistributionChartCycling", { userId, fileId })}
        {String(fileId || '').startsWith('strava-')
          ? <ZoneDistributionStrava userId={userId} fileId={fileId} sport="ciclismo" />
          : <ZoneDistributionChartCycling userId={userId} fileId={fileId} />
        }
      </>
    )}
  </div>
)}


 {isRunning && (
  <>


    <div className="section-block">
      <Graphics
        fileId={fileId}
        elevationData={[
          data?.session?.total_ascent || 0,
          data?.session?.total_descent || 0,
        ]}
        timestamp={data?.activity?.local_timestamp || ''}
      />

      {hasHeartrate !== false && (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#193255', marginBottom: 6, marginTop: 16 }}>
            {t('elevationProfile.hrTimeChartTitle')}
          </div>
          <StrengthHeartRateHistogram fileId={fileId} />
        </>
      )}
      <WellnessForm athleteId={userId} activityId={activityId} date={date} />
      <SessionAiSummaryPanel
        sport="running"
        athleteId={userId}
        activityId={activityId}
        banister={banister}
        date={date}
        completed={completed}
        metrics={summary}
      />
    </div>
  </>
)}


      {isCycling && (
        <>
          <div className="section-block">
            <h2>{t('activitySummary.laps')}</h2>
            <LapsTableCycling laps={data?.laps || []} fileId={fileId} />
            <LapsTableCycling laps={data.laps} records={data.records} fileId={fileId} />
          </div>
          <SessionAiSummaryPanel
            sport="cycling"
            athleteId={userId}
            activityId={activityId}
            banister={banister}
            completed={completed}
            metrics={summary}
            date={date}
          />
        </>
      )}
      {isOtherSport && (
        <SessionAiSummaryPanel
          sport={sport}
          athleteId={userId}
          activityId={activityId}
          banister={banister}
          completed={completed}
          metrics={summary}
          date={date}
        />
      )}
    </div>
  </div>
 );
};
export default ActivitySummary;
